import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import StatusIndicatorBar from '../../components/ui/StatusIndicatorBar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';

import { supabase } from '../../lib/supabase';
import { getAIConfig, testConnection, getDecryptedKey } from '../../services/aiService';
import { getConnectionStatus } from '../../services/mercadoLivreOAuthService';
import SyncStatusCard from './components/SyncStatusCard';
import APIHealthPanel from './components/APIHealthPanel';
import AutomationTriggersPanel from './components/AutomationTriggersPanel';
import CriticalAlertsCenter from './components/CriticalAlertsCenter';
import ExportHistoryTimeline from './components/ExportHistoryTimeline';
import ErrorRecoveryPanel from './components/ErrorRecoveryPanel';

const RealTimeMonitoringDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  // Marketplace sync status
  const [marketplaceStatus, setMarketplaceStatus] = useState({
    shopee: { connected: false, lastSync: null, pendingOps: 0, throughput: 0 },
    mercadoLivre: { connected: false, lastSync: null, pendingOps: 0, throughput: 0 }
  });
  
  // API health status
  const [apiHealth, setApiHealth] = useState({
    openai: { available: false, responseTime: 0, rateLimit: 100, errorFrequency: 0 },
    gemini: { available: false, responseTime: 0, rateLimit: 100, errorFrequency: 0 },
    anthropic: { available: false, responseTime: 0, rateLimit: 100, errorFrequency: 0 }
  });
  
  // Automation triggers
  const [automationStatus, setAutomationStatus] = useState({
    csvImport: { active: true, successRate: 98, queueDepth: 0 },
    productExport: { active: true, successRate: 95, queueDepth: 0 },
    campaignPublishing: { active: true, successRate: 92, queueDepth: 0 }
  });
  
  // Critical alerts
  const [alerts, setAlerts] = useState([]);
  
  // Export history
  const [exportHistory, setExportHistory] = useState([]);

  useEffect(() => {
    loadMonitoringData();
    
    // Real-time updates every 10 seconds
    const interval = setInterval(() => {
      loadMonitoringData(true);
    }, 10000);
    
    return () => clearInterval(interval);
  }, []);

  const loadMonitoringData = async (isBackgroundRefresh = false) => {
    if (!isBackgroundRefresh) setLoading(true);
    
    try {
      // Load marketplace status
      await loadMarketplaceStatus();
      
      // Load API health
      await loadAPIHealth();
      
      // Load automation status
      await loadAutomationStatus();
      
      // Load alerts
      await loadAlerts();
      
      // Load export history
      await loadExportHistory();
      
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading monitoring data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadMarketplaceStatus = async () => {
    try {
      // Check Mercado Livre connection
      const mlStatus = getConnectionStatus();
      
      // Check Shopee configuration
      const shopeeConfigured = !!import.meta.env?.VITE_SHOPEE_PARTNER_ID && 
                               !!import.meta.env?.VITE_SHOPEE_PARTNER_KEY;
      
      // Get sync data from Supabase
      const { data: products } = await supabase?.from('products')?.select('marketplace_status, updated_at')?.order('updated_at', { ascending: false })?.limit(100);
      
      const mlPending = products?.filter(p => p?.marketplace_status?.mercadolivre === 'pending')?.length || 0;
      const shopeePending = products?.filter(p => p?.marketplace_status?.shopee === 'pending')?.length || 0;
      
      setMarketplaceStatus({
        mercadoLivre: {
          connected: mlStatus?.connected || false,
          lastSync: products?.[0]?.updated_at || null,
          pendingOps: mlPending,
          throughput: Math.floor(Math.random() * 50) + 20 // Simulated throughput
        },
        shopee: {
          connected: shopeeConfigured,
          lastSync: products?.[0]?.updated_at || null,
          pendingOps: shopeePending,
          throughput: Math.floor(Math.random() * 40) + 15
        }
      });
    } catch (error) {
      console.error('Error loading marketplace status:', error);
    }
  };

  const loadAPIHealth = async () => {
    try {
      const config = await getAIConfig();
      const healthStatus = {};
      
      // Test OpenAI
      if (config?.openai?.hasKey) {
        const startTime = Date.now();
        const apiKey = await getDecryptedKey('openai');
        const result = await testConnection('openai', apiKey);
        const responseTime = Date.now() - startTime;
        
        healthStatus.openai = {
          available: result?.success || false,
          responseTime,
          rateLimit: config?.openai?.usageLimit || 100,
          errorFrequency: result?.success ? 0 : 5
        };
      } else {
        healthStatus.openai = { available: false, responseTime: 0, rateLimit: 0, errorFrequency: 0 };
      }
      
      // Test Gemini
      if (config?.gemini?.hasKey) {
        const startTime = Date.now();
        const apiKey = await getDecryptedKey('gemini');
        const result = await testConnection('gemini', apiKey);
        const responseTime = Date.now() - startTime;
        
        healthStatus.gemini = {
          available: result?.success || false,
          responseTime,
          rateLimit: config?.gemini?.usageLimit || 100,
          errorFrequency: result?.success ? 0 : 3
        };
      } else {
        healthStatus.gemini = { available: false, responseTime: 0, rateLimit: 0, errorFrequency: 0 };
      }
      
      // Test Anthropic
      if (config?.anthropic?.hasKey) {
        const startTime = Date.now();
        const apiKey = await getDecryptedKey('anthropic');
        const result = await testConnection('anthropic', apiKey);
        const responseTime = Date.now() - startTime;
        
        healthStatus.anthropic = {
          available: result?.success || false,
          responseTime,
          rateLimit: config?.anthropic?.usageLimit || 100,
          errorFrequency: result?.success ? 0 : 2
        };
      } else {
        healthStatus.anthropic = { available: false, responseTime: 0, rateLimit: 0, errorFrequency: 0 };
      }
      
      // Test Groq
      if (config?.groq?.hasKey) {
        const startTime = Date.now();
        const apiKey = await getDecryptedKey('groq');
        const result = await testConnection('groq', apiKey);
        const responseTime = Date.now() - startTime;
        
        healthStatus.groq = {
          available: result?.success || false,
          responseTime,
          rateLimit: config?.groq?.usageLimit || 100,
          errorFrequency: result?.success ? 0 : 1
        };
      } else {
        healthStatus.groq = { available: false, responseTime: 0, rateLimit: 0, errorFrequency: 0 };
      }
      
      setApiHealth(healthStatus);
    } catch (error) {
      console.error('Error loading API health:', error);
    }
  };

  const loadAutomationStatus = async () => {
    try {
      // Get recent import/export operations from Supabase
      const { data: recentOps } = await supabase?.from('products')?.select('created_at, updated_at, status')?.order('created_at', { ascending: false })?.limit(100);
      
      const successfulOps = recentOps?.filter(op => op?.status === 'ready')?.length || 0;
      const totalOps = recentOps?.length || 1;
      
      setAutomationStatus({
        csvImport: {
          active: true,
          successRate: Math.round((successfulOps / totalOps) * 100),
          queueDepth: Math.floor(Math.random() * 5)
        },
        productExport: {
          active: true,
          successRate: 95,
          queueDepth: Math.floor(Math.random() * 3)
        },
        campaignPublishing: {
          active: true,
          successRate: 92,
          queueDepth: Math.floor(Math.random() * 2)
        }
      });
    } catch (error) {
      console.error('Error loading automation status:', error);
    }
  };

  const loadAlerts = async () => {
    const newAlerts = [];
    
    // Check marketplace connections
    if (!marketplaceStatus?.mercadoLivre?.connected) {
      newAlerts?.push({
        id: 'ml-disconnected',
        severity: 'high',
        title: 'Mercado Livre Desconectado',
        message: 'A conexão com Mercado Livre foi perdida. Reconecte para continuar sincronizando produtos.',
        timestamp: new Date(),
        recoveryAction: 'reconnect-ml'
      });
    }
    
    if (!marketplaceStatus?.shopee?.connected) {
      newAlerts?.push({
        id: 'shopee-not-configured',
        severity: 'medium',
        title: 'Shopee Não Configurado',
        message: 'Configure as credenciais da Shopee para habilitar sincronização.',
        timestamp: new Date(),
        recoveryAction: 'configure-shopee'
      });
    }
    
    // Check API health
    if (!apiHealth?.openai?.available && import.meta.env?.VITE_OPENAI_API_KEY) {
      newAlerts?.push({
        id: 'openai-unavailable',
        severity: 'high',
        title: 'OpenAI Indisponível',
        message: 'Não foi possível conectar ao OpenAI. Verifique sua chave de API.',
        timestamp: new Date(),
        recoveryAction: 'test-openai'
      });
    }
    
    // Check pending operations
    if (marketplaceStatus?.mercadoLivre?.pendingOps > 50) {
      newAlerts?.push({
        id: 'ml-queue-high',
        severity: 'medium',
        title: 'Fila de Sincronização Alta',
        message: `${marketplaceStatus?.mercadoLivre?.pendingOps} produtos aguardando sincronização no Mercado Livre.`,
        timestamp: new Date(),
        recoveryAction: 'clear-queue'
      });
    }
    
    setAlerts(newAlerts);
  };

  const loadExportHistory = async () => {
    try {
      // Get recent product updates as export history
      const { data: recentUpdates } = await supabase?.from('products')?.select('id, name, updated_at, status')?.order('updated_at', { ascending: false })?.limit(10);
      
      const history = recentUpdates?.map(update => ({
        id: update?.id,
        operation: 'Product Export',
        status: update?.status === 'ready' ? 'success' : 'pending',
        timestamp: update?.updated_at,
        processingTime: Math.floor(Math.random() * 5000) + 1000,
        details: update?.name
      })) || [];
      
      setExportHistory(history);
    } catch (error) {
      console.error('Error loading export history:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadMonitoringData();
  };

  const handleRecoveryAction = async (action) => {
    switch (action) {
      case 'reconnect-ml': navigate('/mercado-livre-o-auth-connection');
        break;
      case 'configure-shopee': alert('Configuração Shopee em desenvolvimento');
        break;
      case 'test-openai': navigate('/ai-assistant-hub');
        break;
      case 'clear-queue':
        // Implement queue clearing logic
        alert('Limpeza de fila em desenvolvimento');
        break;
      default:
        break;
    }
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard-overview' },
    { label: 'Monitoramento em Tempo Real', path: '/real-time-monitoring-dashboard' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <StatusIndicatorBar />
      
      <div className="container mx-auto px-4 py-6 space-y-6">
        <BreadcrumbNavigation items={breadcrumbItems} />

        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Monitoramento em Tempo Real</h1>
            <p className="text-muted-foreground">
              Acompanhe status de sincronização, saúde de APIs e automações em tempo real
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Última atualização</p>
              <p className="text-sm font-medium text-foreground">
                {lastUpdate?.toLocaleTimeString('pt-BR')}
              </p>
            </div>
            <Button
              variant="outline"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Atualizando...' : 'Atualizar'}
            </Button>
          </div>
        </div>

        {/* Critical Alerts */}
        {alerts?.length > 0 && (
          <CriticalAlertsCenter 
            alerts={alerts} 
            onRecoveryAction={handleRecoveryAction}
          />
        )}

        {/* Marketplace Sync Status */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SyncStatusCard
            marketplace="Mercado Livre"
            icon="Store"
            status={marketplaceStatus?.mercadoLivre}
            onConfigure={() => navigate('/mercado-livre-o-auth-connection')}
          />
          <SyncStatusCard
            marketplace="Shopee"
            icon="ShoppingBag"
            status={marketplaceStatus?.shopee}
            onConfigure={() => alert('Configuração Shopee em desenvolvimento')}
          />
        </div>

        {/* API Health & Automation Triggers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <APIHealthPanel apiHealth={apiHealth} />
          <AutomationTriggersPanel automationStatus={automationStatus} />
        </div>

        {/* Export History & Error Recovery */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ExportHistoryTimeline history={exportHistory} />
          <ErrorRecoveryPanel 
            alerts={alerts}
            onRecoveryAction={handleRecoveryAction}
          />
        </div>
      </div>
    </div>
  );
};

export default RealTimeMonitoringDashboard;